import { useContext, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import Grid from "@mui/material/Grid";

import {
    colors,
    ExpandMoreIcon,
    ReplayIcon,
    TaskCancelIcon
} from "@dotnet/telematics-design-system";

import { useDialog, useMenu } from "components/Actions/ActionBar/hooks";
import { useTaskTypes } from "components/Actions/dialogs/Devices/CreateTask/hooks";
import ActionsFromButton, {
    ACTIONS
} from "components/Actions/dialogs/TaskGroups/ActionFromButton";
import { TASK_GROUP_ACTIONS } from "components/Actions/types";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import ListChoice from "components/DesignComponents/ListChoice";
import Menu, {
    centeredTransformOrigin
} from "components/DesignComponents/Menu";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import TableContext from "context/table/tableContext";
import {
    BatchTaskStatus,
    COLUMN_RESOURCES,
    isFailedOrCompletedWithErrorsTask,
    isPendingOrRunningTask,
    useFormattedNumber,
    useOverflow,
    usePermissions
} from "shared";

import Subtitle from "../Subtitle";
import { HeaderProps } from "../types";
import Loader from "./Loader";

const Header = ({ taskTableData }: HeaderProps) => {
    const { gray900, textPlaceholder } = colors;

    const { detailsData, updateSingleRowData } = useContext(TableContext);

    const { t } = useTranslation();
    const { isRetriableTaskType, isTaskFileDeleted } = useTaskTypes();
    const { isOpenDialog, openDialog, closeDialog } = useDialog();
    const { ref, isOverflowing } = useOverflow();
    const { renderFormattedNumber } = useFormattedNumber();
    const { anchorEl, handleOpenMenu, handleCloseMenu } = useMenu();

    const {
        isReadTaskNotAllowed,
        isRetryTaskNotAllowed,
        isUpdateTaskNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const { isInnerTableLoading, rows, totalRows } = taskTableData;
    const { id, task_type, type, task_stats, file, file_id } = detailsData;

    const hasInnerTableStartedLoadingRef = useRef(false);

    const [isLocalLoading, setIsLocalLoading] = useState(true);

    useEffect(() => {
        setIsLocalLoading(true);
        hasInnerTableStartedLoadingRef.current = false;
    }, [id]);

    useEffect(() => {
        if (isInnerTableLoading) {
            hasInnerTableStartedLoadingRef.current = true;
        }

        if (!isInnerTableLoading && hasInnerTableStartedLoadingRef.current) {
            setIsLocalLoading(false);
        }
    }, [isInnerTableLoading]);

    const pendingAndRunningStatsCount: BatchTaskStatus[] = task_stats.filter(
        ({ status_id }: BatchTaskStatus) => isPendingOrRunningTask(status_id)
    );

    const failedAndCompletedWithErrorsStatsCount: BatchTaskStatus[] =
        task_stats.filter(({ status_id }: BatchTaskStatus) =>
            isFailedOrCompletedWithErrorsTask(status_id)
        );

    const isCancelBtnRendered = pendingAndRunningStatsCount.length > 0;

    const isRetryBtnRendered =
        isRetriableTaskType(task_type) &&
        failedAndCompletedWithErrorsStatsCount.length > 0 &&
        !isTaskFileDeleted(file_id, file);

    const isActionsBtnDisabled = !isCancelBtnRendered && !isRetryBtnRendered;

    const cancelTasksCount = pendingAndRunningStatsCount;
    const retryTasksCount = failedAndCompletedWithErrorsStatsCount;

    const batchTitle = task_type
        ? t(`General##${task_type}`)
        : t(`Task groups##bulk type##${type}`);

    return (
        <Grid
            container
            columnSpacing={3}
            css={css({
                paddingRight: "52px",
                marginBottom: "4px",

                "@media(max-width: 839px)": {
                    paddingRight: "0"
                }
            })}
        >
            <Grid size={{ xs0: 12, md: 7 }}>
                <div
                    css={css({
                        display: "flex",

                        "& > span": {
                            color: gray900,
                            fontSize: "24px",
                            fontWeight: "500",
                            lineHeight: "32px"
                        },

                        "@media (max-width: 839px)": {
                            marginRight: "52px"
                        }
                    })}
                >
                    {!isReadTaskNotAllowed && isLocalLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <TooltipGeneral
                                placement={TOOLTIP_PLACEMENT.Top}
                                title={batchTitle}
                                disableHoverListener={!isOverflowing}
                            >
                                <span
                                    ref={ref}
                                    css={css({
                                        marginRight: "5px",
                                        whiteSpace: "nowrap",
                                        overflowX: "hidden",
                                        textOverflow: "ellipsis"
                                    })}
                                >
                                    {batchTitle}
                                </span>
                            </TooltipGeneral>

                            {!isReadTaskNotAllowed && (
                                <span
                                    css={css({
                                        color: textPlaceholder
                                    })}
                                >
                                    {`(${renderFormattedNumber(totalRows)})`}
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div>
                    <Subtitle
                        data={{
                            [COLUMN_RESOURCES.Schedule]:
                                rows[0]?.[COLUMN_RESOURCES.Schedule],
                            ...detailsData
                        }}
                        isLoading={isLocalLoading}
                        file={file}
                        fileId={file_id}
                    />
                </div>
            </Grid>

            <Grid
                container
                justifyContent="flex-end"
                alignItems="center"
                flexWrap="nowrap"
                size={{ xs0: 12, md: 5 }}
                css={css({
                    "@media(max-width: 839px)": {
                        alignItems: "flex-end",
                        justifyContent: "flex-start"
                    }
                })}
            >
                <div onClick={e => e.stopPropagation()}>
                    <TooltipGeneral
                        placement={TOOLTIP_PLACEMENT.Top}
                        title={
                            isActionsBtnDisabled
                                ? t("Button##no actions available")
                                : undefined
                        }
                    >
                        <span>
                            <Button
                                color={BUTTON_COLORS.Secondary}
                                size={BUTTON_SIZES.Small}
                                variant={BUTTON_VARIANTS.IconRight}
                                onClick={handleOpenMenu}
                                icon={<ExpandMoreIcon />}
                                disabled={isActionsBtnDisabled}
                                css={css({
                                    "@media(max-width: 839px)": {
                                        marginTop: "16px"
                                    }
                                })}
                            >
                                {t("Button##actions")}
                            </Button>
                        </span>
                    </TooltipGeneral>

                    <Menu
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left"
                        }}
                        transformOrigin={centeredTransformOrigin}
                        css={css({
                            "& .MuiMenu-paper": {
                                minWidth: "211px"
                            }
                        })}
                    >
                        {isRetryBtnRendered && (
                            <ListChoice
                                title={t("Button##retry failed tasks")}
                                iconLeft={<ReplayIcon />}
                                disabled={isRetryTaskNotAllowed}
                                tooltipText={getNoPermissionTooltipTitle(
                                    PERMISSIONS.TaskRetry
                                )}
                                onClick={() =>
                                    openDialog(
                                        TASK_GROUP_ACTIONS.RetryFailedTasks
                                    )
                                }
                            />
                        )}

                        {isCancelBtnRendered && (
                            <ListChoice
                                title={t("Button##cancel tasks")}
                                iconLeft={<TaskCancelIcon />}
                                disabled={isUpdateTaskNotAllowed}
                                tooltipText={getNoPermissionTooltipTitle(
                                    PERMISSIONS.TaskUpdate
                                )}
                                onClick={() =>
                                    openDialog(TASK_GROUP_ACTIONS.CancelTasks)
                                }
                            />
                        )}
                    </Menu>

                    {/* Cancels all Pending and Running tasks  */}
                    <ActionsFromButton
                        action={ACTIONS.CancelAllCancelableTasks}
                        taskGroupId={id}
                        data={cancelTasksCount}
                        isOpen={isOpenDialog[TASK_GROUP_ACTIONS.CancelTasks]}
                        updateSingleRowData={updateSingleRowData}
                        close={() =>
                            closeDialog(TASK_GROUP_ACTIONS.CancelTasks)
                        }
                        closeMenu={handleCloseMenu}
                    />

                    {/* Retries all retriable tasks */}
                    <ActionsFromButton
                        action={ACTIONS.RetryFailedTasks}
                        taskGroupId={id}
                        data={retryTasksCount}
                        isOpen={
                            isOpenDialog[TASK_GROUP_ACTIONS.RetryFailedTasks]
                        }
                        updateSingleRowData={updateSingleRowData}
                        close={() =>
                            closeDialog(TASK_GROUP_ACTIONS.RetryFailedTasks)
                        }
                        closeMenu={handleCloseMenu}
                    />
                </div>
            </Grid>
        </Grid>
    );
};

export default Header;
